import { useState, useEffect } from "react";
import {
  FiSearch,
  FiActivity,
  FiCpu,
  FiCheck,
  FiLoader,
  FiMic,
  FiImage,
} from "react-icons/fi";
import { ProgressData } from "../../../../../services/assistant";

interface WorkoutAnalysisProgressProps {
  readonly progress: ProgressData | undefined;
}

export function WorkoutAnalysisProgress({
  progress,
}: WorkoutAnalysisProgressProps) {
  const [currentStep, setCurrentStep] = useState<ProgressData | null>(null);

  useEffect(() => {
    if (progress) {
      setCurrentStep(progress);
    }
  }, [progress]);

  if (!currentStep) {
    return null;
  }

  const getStepInfo = (step: string) => {
    switch (step) {
      case "transcrevendo_audio":
        return {
          icon: FiMic,
          title: "Transcrevendo Áudio",
          description: "Convertendo sua mensagem de áudio em texto...",
          color: "text-blue-600 dark:text-blue-400",
          bgColor: "bg-blue-50 dark:bg-blue-900/20",
          borderColor: "border-blue-200 dark:border-blue-800",
        };
      case "audio_transcrito":
        return {
          icon: FiMic,
          title: "Áudio Transcrito",
          description: "Áudio convertido com sucesso!",
          color: "text-green-600 dark:text-green-400",
          bgColor: "bg-green-50 dark:bg-green-900/20",
          borderColor: "border-green-200 dark:border-green-800",
        };
      case "analisando_imagem":
        return {
          icon: FiImage,
          title: "Analisando Imagem",
          description: "Extraindo informações da imagem enviada...",
          color: "text-blue-600 dark:text-blue-400",
          bgColor: "bg-blue-50 dark:bg-blue-900/20",
          borderColor: "border-blue-200 dark:border-blue-800",
        };
      case "imagem_analisada":
        return {
          icon: FiImage,
          title: "Imagem Analisada",
          description: "Informações da imagem extraídas com sucesso!",
          color: "text-green-600 dark:text-green-400",
          bgColor: "bg-green-50 dark:bg-green-900/20",
          borderColor: "border-green-200 dark:border-green-800",
        };
      case "buscando_exercicios":
        return {
          icon: FiSearch,
          title: "Buscando Exercícios",
          description:
            "Analisando a base de dados para encontrar exercícios relevantes...",
          color: "text-blue-600 dark:text-blue-400",
          bgColor: "bg-blue-50 dark:bg-blue-900/20",
          borderColor: "border-blue-200 dark:border-blue-800",
        };
      case "exercicios_encontrados":
        return {
          icon: FiActivity,
          title: "Exercícios Encontrados",
          description: `Encontramos ${currentStep.exercises_count} exercício${
            currentStep.exercises_count === 1 ? "" : "s"
          } relevante${
            currentStep.exercises_count === 1 ? "" : "s"
          } para seu treino!`,
          color: "text-green-600 dark:text-green-400",
          bgColor: "bg-green-50 dark:bg-green-900/20",
          borderColor: "border-green-200 dark:border-green-800",
        };
      case "processando_ia":
        return {
          icon: FiCpu,
          title: "Processando com IA",
          description:
            "A inteligência artificial está criando seu plano de treino personalizado...",
          color: "text-purple-600 dark:text-purple-400",
          bgColor: "bg-purple-50 dark:bg-purple-900/20",
          borderColor: "border-purple-200 dark:border-purple-800",
        };
      case "finalizando":
        return {
          icon: FiCheck,
          title: "Finalizando",
          description:
            "Quase lá! Estamos finalizando os detalhes do seu treino...",
          color: "text-indigo-600 dark:text-indigo-400",
          bgColor: "bg-indigo-50 dark:bg-indigo-900/20",
          borderColor: "border-indigo-200 dark:border-indigo-800",
        };
      default:
        return {
          icon: FiLoader,
          title: "Processando",
          description: currentStep.message || "Processando sua solicitação...",
          color: "text-gray-600 dark:text-gray-400",
          bgColor: "bg-gray-50 dark:bg-gray-900/20",
          borderColor: "border-gray-200 dark:border-gray-800",
        };
    }
  };

  const stepInfo = getStepInfo(currentStep.step);
  const Icon = stepInfo.icon;

  return (
    <div
      className={`mt-4 rounded-xl border-2 ${stepInfo.borderColor} ${stepInfo.bgColor} p-4 transition-all duration-300 animate-in fade-in slide-in-from-bottom-2`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${stepInfo.bgColor} ${stepInfo.color}`}
        >
          <Icon
            className={`h-5 w-5 ${
              currentStep.step === "processando_ia" ||
              currentStep.step === "transcrevendo_audio" ||
              currentStep.step === "analisando_imagem"
                ? "animate-spin"
                : ""
            }`}
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className={`text-sm font-bold ${stepInfo.color} mb-1`}>
            {stepInfo.title}
          </h4>
          <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
            {stepInfo.description}
          </p>
          {currentStep.exercises_count > 0 && (
            <div className="mt-2 flex items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-white/60 dark:bg-gray-800/60 px-2.5 py-1 text-xs font-semibold text-gray-700 dark:text-gray-300">
                <FiActivity className="h-3 w-3" />
                {currentStep.exercises_count}{" "}
                {currentStep.exercises_count === 1
                  ? "exercício encontrado"
                  : "exercícios encontrados"}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
